import { useCallback } from 'react';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';

/**
 * Custom hook for CSV export functionality
 */
export function useExport(shows, generalExpenses, inventory) {
  // CSV Export Function
  const exportToCSV = useCallback((data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    let csvContent = headers.join(',') + '\n';

    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        return typeof value === 'string' && (value.includes(',') || value.includes('"'))
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      });
      csvContent += values.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success(`${filename} exported successfully!`);
  }, []);

  const handleExportShows = useCallback(() => {
    const showsData = [];
    Object.keys(shows).forEach(dateKey => {
      shows[dateKey].forEach(show => {
        showsData.push({
          Date: dateKey,
          Title: show.title || '',
          Type: show.eventType || 'show',
          Venue: show.venue || '',
          City: show.city || '',
          'Load In': show.loadInTime || '',
          'Show Time': show.showTime || '',
          Guarantee: show.guarantee || '0',
          'Cash Merch': show.cashMerch || '0',
          'Venmo Merch': show.venmoMerch || '0',
          Gas: show.gas || '0',
          Hotel: show.hotel || '0',
          Food: show.food || '0',
          Tolls: show.tolls || '0',
          'Other Expenses': show.otherExpenses || '0',
          Notes: show.showNotes || ''
        });
      });
    });
    exportToCSV(showsData, 'shows');
  }, [shows, exportToCSV]);

  const handleExportExpenses = useCallback(() => {
    const expensesData = generalExpenses.map(expense => ({
      Date: expense.date,
      Category: expense.category || 'Other',
      Description: expense.description,
      Amount: expense.amount
    }));
    exportToCSV(expensesData, 'expenses');
  }, [generalExpenses, exportToCSV]);

  const handleExportInventory = useCallback(() => {
    const inventoryData = [];
    inventory.forEach(item => {
      if (item.hasSizes && item.size && typeof item.size === 'string' && item.size.startsWith('[')) {
        try {
          const sizes = JSON.parse(item.size);
          sizes.forEach(sizeData => {
            inventoryData.push({
              Item: item.item,
              Size: sizeData.size,
              SKU: sizeData.sku || '',
              Quantity: sizeData.quantity,
              Cost: item.cost || item.msrp,
              'Sell Price': item.sellPrice,
              'Total Value': (parseInt(sizeData.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
            });
          });
        } catch (e) {
          inventoryData.push({
            Item: item.item,
            Size: item.size,
            SKU: item.sku || '',
            Quantity: item.quantity,
            Cost: item.cost || item.msrp,
            'Sell Price': item.sellPrice,
            'Total Value': (parseInt(item.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
          });
        }
      } else {
        inventoryData.push({
          Item: item.item,
          Size: item.size,
          SKU: item.sku || '',
          Quantity: item.quantity,
          Cost: item.cost || item.msrp,
          'Sell Price': item.sellPrice,
          'Total Value': (parseInt(item.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
        });
      }
    });
    exportToCSV(inventoryData, 'inventory');
  }, [inventory, exportToCSV]);

  const handleExportAll = useCallback(() => {
    const showsData = [];
    Object.keys(shows).forEach(dateKey => {
      shows[dateKey].forEach(show => {
        showsData.push({
          Date: dateKey,
          Title: show.title || '',
          Type: show.eventType || 'show',
          Venue: show.venue || '',
          City: show.city || '',
          'Load In': show.loadInTime || '',
          'Show Time': show.showTime || '',
          Guarantee: show.guarantee || '0',
          'Cash Merch': show.cashMerch || '0',
          'Venmo Merch': show.venmoMerch || '0',
          Gas: show.gas || '0',
          Hotel: show.hotel || '0',
          Food: show.food || '0',
          Tolls: show.tolls || '0',
          'Other Expenses': show.otherExpenses || '0'
        });
      });
    });

    const expensesData = generalExpenses.map(expense => ({
      Date: expense.date,
      Category: expense.category || 'Other',
      Description: expense.description,
      Amount: expense.amount
    }));

    const inventoryData = [];
    inventory.forEach(item => {
      if (item.hasSizes && item.size && typeof item.size === 'string' && item.size.startsWith('[')) {
        try {
          const sizes = JSON.parse(item.size);
          sizes.forEach(sizeData => {
            inventoryData.push({
              Item: item.item,
              Size: sizeData.size,
              SKU: sizeData.sku || '',
              Quantity: sizeData.quantity,
              Cost: item.cost || item.msrp,
              'Sell Price': item.sellPrice,
              'Total Value': (parseInt(sizeData.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
            });
          });
        } catch (e) {
          inventoryData.push({
            Item: item.item,
            Size: item.size,
            SKU: item.sku || '',
            Quantity: item.quantity,
            Cost: item.cost || item.msrp,
            'Sell Price': item.sellPrice,
            'Total Value': (parseInt(item.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
          });
        }
      } else {
        inventoryData.push({
          Item: item.item,
          Size: item.size,
          SKU: item.sku || '',
          Quantity: item.quantity,
          Cost: item.cost || item.msrp,
          'Sell Price': item.sellPrice,
          'Total Value': (parseInt(item.quantity || 0) * parseFloat(item.sellPrice || 0)).toFixed(2)
        });
      }
    });

    if (showsData.length > 0) exportToCSV(showsData, 'shows');
    if (expensesData.length > 0) exportToCSV(expensesData, 'expenses');
    if (inventoryData.length > 0) exportToCSV(inventoryData, 'inventory');

    if (showsData.length === 0 && expensesData.length === 0 && inventoryData.length === 0) {
      toast.error('No data to export');
    } else {
      toast.success('All data exported successfully!');
    }
  }, [shows, generalExpenses, inventory, exportToCSV]);

  return {
    handleExportShows,
    handleExportExpenses,
    handleExportInventory,
    handleExportAll,
  };
}

export default useExport;